import type { MarketingConsent } from "./consent";

type Utm = Partial<Record<"source" | "medium" | "campaign" | "content" | "term", string>>;
const UTM_KEYS = ["source", "medium", "campaign", "content", "term"] as const;
const PRODUCT_PATH = /^\/(?:practice(?:\/|$)|today(?:\/|$)|insights(?:\/|$)|isee(?:\/|$)|sat\/(?!kg-mock(?:\/|$)|info(?:\/|$))|programs(?:\/|$)|cb-preview(?:\/|$)|vocab-mastery(?:\/|$)|next\/app(?:\/|$))/;
const UTM_VALUE = /^[A-Za-z0-9._-]{1,100}$/;

// A raw control-character regex charset trips `no-control-regex` even when
// written via `\u00xx` escapes (the rule inspects the pattern's resulting
// code points, not the source spelling). Check char codes directly instead --
// identical matching semantics, no control-character regex range.
function hasControlOrBackslash(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || value[i] === '\\') return true;
  }
  return false;
}

function safeReturnTo(candidate: string | undefined) {
  if (!candidate || !PRODUCT_PATH.test(candidate)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return null;
  }
  if (decoded.startsWith("//") || hasControlOrBackslash(decoded)) return null;
  try {
    const parsed = new URL(candidate, "https://product.invalid");
    return parsed.origin === "https://product.invalid" ? `${parsed.pathname}${parsed.search}${parsed.hash}` : null;
  } catch {
    return null;
  }
}

export function createMarketingTransition(options: {
  consent: MarketingConsent;
  productOrigin: string;
  returnTo?: string;
  destinationPath?: "/login" | "/practice";
  utm?: Utm;
  uuid?: () => `${string}-${string}-${string}-${string}-${string}`;
}) {
  if (options.consent !== "granted") return null;
  const returnTo = options.returnTo === undefined ? null : safeReturnTo(options.returnTo);
  if (options.returnTo !== undefined && !returnTo) return null;
  const transitionId = (options.uuid ?? (() => crypto.randomUUID()))();
  const url = new URL(options.destinationPath ?? "/login", options.productOrigin);
  url.searchParams.set("transition_id", transitionId);
  if (returnTo) url.searchParams.set("returnTo", returnTo);
  for (const key of UTM_KEYS) {
    const value = options.utm?.[key];
    if (value && UTM_VALUE.test(value)) url.searchParams.set(`utm_${key}`, value);
  }
  return { transitionId, href: url.href };
}
